import React, { useCallback, useEffect, useMemo } from "react"; // Adicionado useMemo
import MainLayout from "../../../../layouts/MainLayout";
import Form from "../../../../components/forms/Form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useNotification from "../../../../hooks/useNotification";
import useLoader from "../../../../hooks/useLoader";
import useForm from "../../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import SectionHeader from "../../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { kitItemFields } from "../../../../constants/forms/kitFields";
import useUrlFilters from "../../../../hooks/useUrlFilters";

const CreateKitItemPage = () => {
  const navigate = useNavigate();
  const { equipmentKitId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "equipments_kits_items"
  );

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange } = useForm(
    setDefaultFieldValues(kitItemFields),
    kitItemFields
  );

  // --- Mutação para criar um novo Item do Kit ---
  const createKitItemMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePost(
        entities.equipmentsKits.items.create(equipmentKitId),
        dataToCreate
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        navigate(`/aluguel/kits-equipamentos/${equipmentKitId}/itens`, {
          state: { type: "success" },
        });
        queryClient.invalidateQueries({
          queryKey: [
            "equipments_kits_items",
            equipmentKitId,
            filters,
            pagination,
          ],
        });
      }
    },
    onError: (error) => {
      console.error("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        kitItem: {
          ...cleanedData.kitItem,
        },
      };
      await createKitItemMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao criar item do kit:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createKitItemMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/kits-equipamentos/${equipmentKitId}/itens`);
  }, [navigate, equipmentKitId]);

  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return kitItemFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Criação do Item do Kit"
        showBackButton={true}
        backUrl={`/aluguel/kits-equipamentos/${equipmentKitId}/itens`}
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar Item"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createKitItemMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateKitItemPage;
